import { useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Download, Trash2, Search, Flame } from "lucide-react";
import { toast } from "sonner";

// A lead is "hot" when the guest showed strong intent: placed a date hold or downloaded a quote/album/decor estimate.
const isHot = (l) => {
  const et = (l.event_type || "").toLowerCase();
  return et.includes("hold") || et.includes("quote") || et.includes("decor") || (l.message || "").includes("hold: Yes");
};

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [hotOnly, setHotOnly] = useState(false);

  const load = () => api.get("/admin/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    load();
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/leads.csv`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "leads.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const hotCount = leads.filter(isHot).length;
  const filtered = leads.filter((l) => {
    if (hotOnly && !isHot(l)) return false;
    if (!q) return true;
    const s = q.toLowerCase();
    return [l.name, l.phone, l.email, l.event_type, l.property_interested, l.message].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Leads</div>
          <h1 className="font-display text-4xl mt-3">Enquiries</h1>
        </div>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 bg-zinc-900 text-white px-4 py-2 text-sm rounded-md" data-testid="export-csv"><Download className="w-4 h-4" /> Export CSV</button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-9" data-testid="lead-search" />
        </div>
        <button
          onClick={() => setHotOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border transition-colors ${hotOnly ? "bg-amber-500 border-amber-500 text-white" : "border-zinc-300 text-zinc-600 hover:bg-amber-50"}`}
          data-testid="hot-leads-filter"
        >
          <Flame className="w-3.5 h-3.5" /> Hot leads ({hotCount})
        </button>
      </div>

      <div className="mt-6 bg-white border border-zinc-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="p-3 text-left">Date</th>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">Phone</th>
              <th className="p-3 text-left">Email</th>
              <th className="p-3 text-left">Event</th>
              <th className="p-3 text-left">Property</th>
              <th className="p-3 text-left">Guests</th>
              <th className="p-3 text-left">UTM</th>
              <th className="p-3 text-left">Source</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan={10} className="p-6 text-center text-zinc-500">No leads.</td></tr>}
            {filtered.map((l) => (
              <tr key={l.id} className="border-t border-zinc-100 align-top" data-testid={`lead-row-${l.id}`}>
                <td className="p-3 text-zinc-500 whitespace-nowrap">{new Date(l.created_at).toLocaleDateString()}</td>
                <td className="p-3 font-medium">
                  <span className="inline-flex items-center gap-1.5">
                    {isHot(l) && <Flame className="w-3.5 h-3.5 text-amber-500 shrink-0" title="Hot lead — strong intent" />}
                    {l.name}
                  </span>
                </td>
                <td className="p-3">{l.phone}</td>
                <td className="p-3">{l.email}</td>
                <td className="p-3">{l.event_type}</td>
                <td className="p-3">{l.property_interested}</td>
                <td className="p-3">{l.guest_count}</td>
                <td className="p-3 text-xs text-zinc-500">{[l.utm_source, l.utm_medium, l.utm_campaign].filter(Boolean).join(" / ") || "—"}</td>
                <td className="p-3 text-xs text-zinc-500 max-w-[180px] truncate" title={l.source_page_url}>{l.source_page_url || "—"}</td>
                <td className="p-3"><button onClick={() => remove(l.id)} className="text-red-600 hover:bg-red-50 p-1 rounded"><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
